import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import { ShieldCheck, Search, ArrowLeft } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { getDiplomaI18n } from "../i18n/diploma";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyPage = () => {
  const { lang } = useLanguage();
  const T = useMemo(() => getDiplomaI18n(lang), [lang]);
  const [params] = useSearchParams();
  const routeParams = useParams();
  const initial = routeParams.hash || params.get("hash") || params.get("wallet") || "";

  const [input, setInput] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const verify = useCallback(async (val) => {
    const q = (val ?? input).trim();
    if (!q) return;
    setLoading(true);
    setResult(null);
    try {
      const isHash = q.length > 42;
      const url = `${API}/diploma/verify?${isHash ? "hash" : "wallet"}=${encodeURIComponent(q)}`;
      const res = await fetch(url);
      const data = await res.json().catch(() => ({ valid: false }));
      setResult(data);
    } catch {
      setResult({ valid: false, error: "network" });
    }
    setLoading(false);
  }, [input]);

  useEffect(() => {
    if (initial) verify(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main
      className="min-h-screen text-white"
      style={{
        background: "radial-gradient(circle at top, #0d0e1c, #07080f 60%)",
        fontFamily: "'Courier New', monospace",
      }}
      data-testid="verify-page"
    >
      <header className="border-b border-white/5">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/55 hover:text-amber-300">
            <ArrowLeft className="h-4 w-4" /> RE₿EL
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300/70">
            ⛓ BASE L2 BLOCKCHAIN
          </span>
        </div>
      </header>

      <div className="mx-auto flex max-w-2xl flex-col items-center px-4 py-12 md:py-20">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300">
          <ShieldCheck className="h-3 w-3" /> RE₿EL PROOF™
        </div>
        <h1 className="text-center text-2xl font-black tracking-tight text-amber-300 md:text-3xl">
          {T.verifyTitle}
        </h1>
        <p className="mt-2 max-w-md text-center text-sm text-white/55">
          {T.verifySub}
        </p>

        <div className="mt-7 flex w-full max-w-xl flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && verify()}
            placeholder={T.verifyPh}
            data-testid="verify-input"
            className="flex-1 rounded-lg border border-amber-400/30 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-all focus:border-amber-300"
            style={{ fontFamily: "'Courier New', monospace" }}
          />
          <button
            type="button"
            onClick={() => verify()}
            disabled={loading || !input.trim()}
            data-testid="verify-btn"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-3 text-xs font-black uppercase tracking-[0.2em] text-black shadow-[0_0_24px_rgba(245,166,35,0.3)] transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Search className="h-4 w-4" />
            {loading ? T.verifyLoading : T.verifyBtn}
          </button>
        </div>

        {result && (
          <div
            className={`mt-8 w-full max-w-2xl rounded-xl border p-5 ${result.valid ? "border-emerald-400/30 bg-emerald-400/[0.05]" : "border-rose-400/30 bg-rose-400/[0.05]"}`}
            data-testid="verify-result"
          >
            <h2 className={`text-lg font-black tracking-[0.1em] ${result.valid ? "text-emerald-300" : "text-rose-300"}`}>
              {result.valid ? T.verifyValid : T.verifyInvalid}
            </h2>
            {result.valid && result.diplomas?.map((d, i) => (
              <div key={d.id || i} className={i > 0 ? "mt-4 border-t border-white/10 pt-4" : "mt-4"}>
                <div className="grid grid-cols-2 gap-x-5 gap-y-3 md:grid-cols-4">
                  <Field label={T.verifyTitular} value={d.titular} />
                  <Field label={T.verifyNivel} value={(d.nivel_name?.[lang] || d.nivel_name?.es || `Nivel ${d.nivel}`).split(" · ")[0]} />
                  <Field label={T.verifyFecha} value={d.minted_at ? new Date(d.minted_at).toLocaleDateString() : "—"} />
                  <Field label={T.verifyWallet} value={`${(d.wallet_address || "").slice(0, 8)}…${(d.wallet_address || "").slice(-4)}`} mono />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a href={d.basescan_url} target="_blank" rel="noopener noreferrer"
                     className="rounded-md border border-cyan-400/30 bg-cyan-400/10 px-3 py-1.5 text-[11px] font-bold text-cyan-300 hover:bg-cyan-400/20">
                    {T.basescan}
                  </a>
                  <a href={d.opensea_url} target="_blank" rel="noopener noreferrer"
                     className="rounded-md border border-fuchsia-400/30 bg-fuchsia-400/10 px-3 py-1.5 text-[11px] font-bold text-fuchsia-300 hover:bg-fuchsia-400/20">
                    {T.opensea}
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-12 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-white/25">
          {T.verifyFooter}
        </p>
      </div>
    </main>
  );
};

const Field = ({ label, value, mono = false }) => (
  <div>
    <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/35">{label}</div>
    <div className={`mt-1 text-sm text-white/85 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
  </div>
);

export default VerifyPage;
